import type { AgentStatus } from '@reflex/shared';

/**
 * Default cadence for the devbox-status poll. The devbox-status endpoint is a
 * single lightweight Runloop control-plane lookup, and only one sandbox tab is
 * mounted at a time, so an 8s poll is cheap while still clearing the suspend
 * guard promptly after a resume.
 */
export const DEVBOX_STATUS_POLL_MS = 8_000;
/**
 * Faster cadence used while the box is mid-transition. A suspended/suspending
 * box has the `SuspendedDevboxGuard` (and the status badge) actively blocking
 * the surface, and the browser tab's HTTP probe wakes a suspended box
 * *implicitly* — without priming the status cache the way the explicit Resume
 * button does. Polling quickly here clears the guard within a couple of
 * seconds of the box waking instead of stalling for a full
 * {@link DEVBOX_STATUS_POLL_MS} window. We also treat the "on its way up"
 * statuses as transitional so the brief `resuming` window converges just as
 * fast.
 */
export const DEVBOX_STATUS_SETTLING_POLL_MS = 2_000;
/**
 * Raw Runloop statuses where the box is not steadily `running` — either
 * suspended (waiting on a wake) or booting/resuming back up. These are the
 * states the suspend guard blocks on, so the poll runs at the faster
 * {@link DEVBOX_STATUS_SETTLING_POLL_MS} cadence for them.
 */
const DEVBOX_STATUS_SETTLING = new Set([
  'suspended',
  'suspending',
  'resuming',
  'provisioning',
  'initializing',
]);
/**
 * Pick the poll cadence for the current devbox status: the faster "settling"
 * interval while the box is suspended or coming back up, otherwise the base
 * cadence.
 */
export function devboxStatusPollInterval(
  status: string | null | undefined,
  baseMs: number,
): number {
  if (typeof status === 'string' && DEVBOX_STATUS_SETTLING.has(status)) {
    return Math.min(baseMs, DEVBOX_STATUS_SETTLING_POLL_MS);
  }
  return baseMs;
}

/**
 * Whether a box in this raw Runloop status can be explicitly resumed back to
 * `running`. Only a fully `suspended` box qualifies: a `suspending` box must
 * finish suspending first (Runloop rejects a resume with `400 ... not in proper
 * state` until it reaches `DEVBOX_SUSPENDED`), a box already `running` /
 * `resuming` / `provisioning` needs no resume, and a `shutdown` / `failure` box
 * is gone until the agent restarts. Mirrors the server `POST /agents/:id/resume`
 * route, which only calls `resumeDevbox` when the live status is `suspended`.
 *
 * Surfaces that want to proactively wake a box on open (e.g. the dev-server
 * preview iframe) use this to decide whether an explicit resume is warranted.
 */
export function isResumableDevboxStatus(status: string | null | undefined): boolean {
  return status === 'suspended';
}

/**
 * Agent statuses for which the devbox is reliably gone, so polling its live
 * status adds nothing over the agent-status-derived state and just hammers the
 * control plane. Mirrors `deriveDevboxAvailability` / `deriveSandboxState`,
 * where `error`/`stopped`/`terminated` all read as a dead sandbox.
 *
 * `completed`/`interrupted` are intentionally absent: those leave the agent
 * process done but keep the devbox alive (suspendable/resumable) for
 * follow-ups, so the live status is still worth polling.
 */
const DEVBOX_GONE_AGENT_STATUSES: ReadonlySet<string> = new Set<AgentStatus>([
  'stopped',
  'error',
  'terminated',
]);

/**
 * Whether the agent has reached a state where its devbox is gone and
 * unrecoverable without a restart. Sandbox surfaces use this to force a
 * terminal guard state instead of trusting the last polled devbox status,
 * which TanStack keeps cached after the poll is disabled (so a box last seen
 * `suspended` would otherwise keep showing a "Resume" affordance for a dead
 * agent).
 *
 * `agentStatus` is typed as `string` because plugin surfaces receive the agent
 * as a loosely-typed `PluginAgentRef`; the set's contents are still validated
 * against {@link AgentStatus} at definition.
 */
export function isDevboxGoneAgentStatus(agentStatus: string): boolean {
  return DEVBOX_GONE_AGENT_STATUSES.has(agentStatus);
}

/**
 * Whether the devbox-status poll should run for an agent. There must be a
 * devbox that could still be alive and changing, and the agent must not have
 * reached a gone state. Sandbox surfaces pass this as the `enabled` option to
 * `useDevboxStatus` so a mounted-but-idle tab on a dead agent doesn't poll the
 * control plane forever.
 */
export function isDevboxStatusPollEligible(
  agentStatus: string,
  devboxId: string | null | undefined,
): boolean {
  return Boolean(devboxId) && !isDevboxGoneAgentStatus(agentStatus);
}
